import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { GRTable, GRTableColumn, SortOrder } from '../../../../components/GRTable/GRTable'
import { creativesCountColumn, dimensionColumn, gamesCountColumn, genreColumn, motivationChipColumn, tagNameColumn } from '../../helpers/tagColumns'
import { useAllTagsListing } from '../../hooks/useAllTagsListing'
import { useControlledColumns } from '../../hooks/useControlledColumns'
import { useUAInsightsNavigation } from '../../hooks/useUAInsightsNavigation'
import { useUATaxonomy } from '../../hooks/useUATaxonomy'
import { CreativeTag } from '../../types'
import { ListingTruncationNote } from '../ListingTruncationNote/ListingTruncationNote'
import { SectionCard } from '../SectionCard/SectionCard'
import { TableSkeleton } from '../TableSkeleton/TableSkeleton'
import { UAInsightsDialog } from '../UAInsightsDialog/UAInsightsDialog'

const tagRowId = (row: CreativeTag) => row.tag.id

/**
 * Drill-down dialog reached from the "Unique Tags" summary card. Lists every
 * known creative tag (deduped across trending + sunsetting) and reuses the
 * shared `tagColumns` so every cell keeps its existing drill-through
 * (dimension, tag, genre, tag-games, motivation).
 */
export const AllTagsDialog: FC = () => {
  const { t } = useTranslation('ua-insights')
  const { params, close, canGoBack, goBack, openTag, openDimensionTags, openTagGames, openMotivation, openGenreTags } = useUAInsightsNavigation()
  const taxonomy = useUATaxonomy()
  const open = params.dialog === 'all-tags'
  const { data, isLoading, isError, refetch } = useAllTagsListing(open)

  // Deliberately not `creativeTagListingColumns`: this listing leads with the
  // dimension column and ranks by creatives count rather than games count.
  const defaultColumns: GRTableColumn<CreativeTag, undefined>[] = useMemo(
    () => [
      dimensionColumn(t, taxonomy, openDimensionTags),
      tagNameColumn(t, taxonomy, openTag),
      genreColumn(t, taxonomy, openGenreTags),
      creativesCountColumn(t, { align: 'center', onClick: openTag, sortOrder: SortOrder.DESC }),
      gamesCountColumn(t, { align: 'center', onClick: openTagGames }),
      motivationChipColumn(t, taxonomy, openMotivation),
    ],
    [t, taxonomy, openDimensionTags, openTag, openGenreTags, openTagGames, openMotivation]
  )

  const [columns, setColumns] = useControlledColumns(defaultColumns)

  const tags = data?.tags ?? []

  return (
    <UAInsightsDialog
      open={open}
      onClose={close}
      title={t('all_tags_title')}
      canGoBack={canGoBack}
      onGoBack={goBack}
      showNetworkSelector
      hasData={!!data}
      isLoading={isLoading}
      isError={isError}
      onRetry={refetch}
      renderChildrenWhenEmpty
      loadingPlaceholder={<TableSkeleton rows={10} columns={6} />}
    >
      <SectionCard title={t('tags_count_section', { count: data?.total ?? tags.length })} disableBodyPadding>
        <GRTable rows={tags} columns={columns} onColumnsUpdated={setColumns} striped hoverable rowIdKey={tagRowId} noRowsLabel={t('no_tags')} />
        <ListingTruncationNote shown={tags.length} total={data?.total} />
      </SectionCard>
    </UAInsightsDialog>
  )
}
